import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { useLiveTimer } from "../../hooks/useTimer";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Play, Plus, Square } from "lucide-react";
import { MOBILE } from "../../constants/testIds";
import { toast } from "sonner";

export default function Today() {
    const nav = useNavigate();
    const [running, setRunning] = useState(null);
    const [runningJob, setRunningJob] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [r, a] = await Promise.all([
                api.get("/v1/time-entries/running").then((x) => x.data).catch(() => null),
                api.get("/v1/assignments/today").then((x) => x.data.assignments).catch(() => []),
            ]);
            setRunning(r);
            setAssignments(a || []);
            if (r?.job_id) {
                const j = await api.get(`/jobs/${r.job_id}`).then((x) => x.data).catch(() => null);
                setRunningJob(j);
            } else {
                setRunningJob(null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const stop = async () => {
        try {
            const { data } = await api.post(`/v1/time-entries/${running.id}/stop`, {});
            nav(`/m/stopped/${data.id}`);
        } catch (e) {
            toast.error("Could not stop timer. Try again.");
        }
    };

    if (loading) return null;

    return (
        <div data-testid={MOBILE.todayRoot} className="px-4 pb-32">
            {running && <RunningCard entry={running} job={runningJob} onStop={stop} />}

            <div className="mt-6 mb-3 flex items-baseline justify-between">
                <div className="eyebrow">Today</div>
                <div className="text-xs text-muted num">
                    {assignments.length} {assignments.length === 1 ? "job" : "jobs"}
                </div>
            </div>

            {!assignments.length && !running && (
                <div className="km-card p-8 text-center text-sm text-muted mt-4">
                    <div className="text-3xl mb-3">🏗️</div>
                    <div>No jobs scheduled for today.</div>
                    <div className="mt-1">Tap Add if you're heading out anyway.</div>
                </div>
            )}

            <ul className="space-y-3">
                {assignments.map((a) => (
                    <li key={a.id}>
                        <JobRow
                            assignment={a}
                            onStart={() => nav(`/m/job/${a.job_id}?type=${a.work_type}`)}
                        />
                    </li>
                ))}
            </ul>

            <button
                data-testid={MOBILE.fabAdd}
                onClick={() => nav("/m/add")}
                className="fixed bottom-[88px] right-5 h-14 pl-4 pr-5 rounded-full bg-[color:var(--km-navy)] text-white flex items-center gap-2 shadow-lg font-medium"
            >
                <Plus size={20} strokeWidth={2.2} />
                Add job or address
            </button>
        </div>
    );
}

function RunningCard({ entry, job, onStop }) {
    const t = useLiveTimer(entry.start_time);
    return (
        <div
            data-testid={MOBILE.runningCard}
            className="mt-4 rounded-2xl p-5 text-white relative overflow-hidden"
            style={{ background: "linear-gradient(150deg,#268254 0%,#14563A 100%)" }}
        >
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] opacity-90">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                Running · {entry.work_type}
            </div>
            <div className="mt-2 font-extrabold text-lg leading-tight">
                {job?.address || entry.unallocated_address || "Unallocated"}
            </div>
            <div className="text-xs opacity-80 num mt-0.5">{job?.job_number || "unallocated"}</div>
            <div className="num text-4xl mt-4 tracking-wider">{t}</div>
            <button
                data-testid={MOBILE.stopBtn}
                onClick={onStop}
                className="mt-4 w-full py-3 rounded-xl bg-white/95 text-[color:var(--km-green-d)] font-bold text-sm flex items-center justify-center gap-2"
            >
                <Square size={14} fill="currentColor" /> Stop &amp; Save
            </button>
        </div>
    );
}

function JobRow({ assignment, onStart }) {
    const j = assignment.job || {};
    return (
        <div
            data-testid={`assignment-${assignment.id}`}
            className="km-card p-3 flex items-center gap-3"
        >
            <div className="w-12 h-12 rounded-lg bg-[color:var(--km-navy-l)] text-[color:var(--km-navy)] flex flex-col items-center justify-center num font-bold text-[13px] leading-none">
                <span className="text-[10px] opacity-70">Job</span>
                <span>{j.job_number || "—"}</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-ink font-medium truncate">{j.address || "Unallocated"}</div>
                <div className="text-xs text-muted flex items-center gap-2 mt-0.5">
                    <StatusBadge status={assignment.work_type} />
                    {j.client_id && <span className="truncate">·</span>}
                </div>
            </div>
            <button
                onClick={onStart}
                className="w-10 h-10 rounded-full bg-[color:var(--km-green)] text-white flex items-center justify-center shrink-0"
                aria-label="Start timer"
            >
                <Play size={16} fill="currentColor" />
            </button>
        </div>
    );
}
